import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
    FormControl,
    Typography,
    Box,
    TextField,
    Button,
    Grid,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    Avatar,
    ListSubheader,
    InputAdornment,
    Popover,
    List,
    ListItem,
    ListItemText,
    Checkbox,
    IconButton,
    CircularProgress,
    Chip
} from '@mui/material';
import { CheckCircle, Search, Close, KeyboardArrowDown } from '@mui/icons-material';
import {
    usePopupState,
    bindTrigger,
    bindPopover,
} from 'material-ui-popup-state/hooks';
import { enqueueSnackbar } from 'notistack';
import { ConfirmationModal } from '../../../../../components/ConfirmationModal';
import { useFetchDataQuery, useUpdateCardStatusMutation } from '../../../services/services';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store/store';
import { encrypt } from '../../../../../utils/encrypt';
import SetPasswordAction from './SetPasswordAction';
import { decrypt } from '../../../../../utils/decrypt';
// Mock card service
interface CardActivateResponse {
    success: boolean;
    message: string;
}

const cardService = {
    activateCard: async (activationInfo: any): Promise<CardActivateResponse> => {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve({
                    success: true,
                    message: 'Kartlar başarıyla aktifleştirildi'
                });
            }, 2500);
        });
    }
};

interface ActivateCardActionProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

// Filtre tanımı: cardType (P) ve status (T,B)
const cardFilters = [
    {
        field: "cardType",
        logicOperator: "and",
        values: ["P"],
        valueType: 1,
        operationType: 6
    },
    {
        field: "status",
        logicOperator: "and",
        values: ["T,B"],
        valueType: 1,
        operationType: 11
    }
];

const ActivateCardAction: React.FC<ActivateCardActionProps> = ({ isOpen, onClose, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [selectedCard, setSelectedCard] = useState<any>(null);
    const [cardSearchText, setCardSearchText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [allCards, setAllCards] = useState<any[]>([]);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const isLoadingMoreRef = useRef<boolean>(false);
    const [expiryMonth, setExpiryMonth] = useState('');
    const [expiryYear, setExpiryYear] = useState('');
    const [cvv, setCvv] = useState('');
    const [isSetPasswordModalOpen, setIsSetPasswordModalOpen] = useState(false);
    const [updateCardStatus] = useUpdateCardStatusMutation();
    const { currentUserState } = useSelector((state: RootState) => state?.currentUserState);
    const listUrl = `/Card/CustomerCardsV2?customerId=${currentUserState?.companyId}`;
    const [requestBody, setRequestBody] = useState({
        filters: cardFilters,
        pageSize: 25,
        page: 1,
        orderBy: "",
        orderDesc: false,
        customerId: currentUserState?.companyId || undefined,
    });

    const popupState = usePopupState({
        variant: 'popover',
        popupId: 'card-activate-popup',
    });

    const toggleCard = (card: any) => {
        if (selectedCard?.cardNo === card.cardNo) {
            setSelectedCard(null);
        } else {
            setSelectedCard(card);
            popupState.close();
        }
    };

    const isCardSelected = (cardNo: string) => {
        return selectedCard?.cardNo === cardNo;
    };
    
    // customerId değiştiğinde requestBody'yi güncelle
    useEffect(() => {
        if (currentUserState?.companyId) {
            setRequestBody(prev => ({ ...prev, customerId: currentUserState.companyId, filters: cardFilters }));
        }
    }, [currentUserState?.companyId]);
    
    const { data: fetchData, isLoading: isFetchLoading, isFetching, refetch } = useFetchDataQuery({ url: listUrl, filter: requestBody, method: "POST" }, { 
        skip: !requestBody?.customerId || !isOpen,
        refetchOnMountOrArgChange: false
    });
    const isRequestLoading = isFetching || isFetchLoading;

    // Modal açıldığında state'leri resetle ve listeyi çek
    useEffect(() => {
        if (isOpen && currentUserState?.companyId) {
            setCurrentPage(1);
            setAllCards([]);
            setHasMore(true);
            setSelectedCard(null);
            setCardSearchText('');
            setIsLoadingMore(false);
            isLoadingMoreRef.current = false;
            setRequestBody(prev => ({ ...prev, page: 1, customerId: currentUserState?.companyId, filters: cardFilters }));
            // Modal açıldığında listeyi çek - Manuel refetch'i kaldırıyoruz çünkü setRequestBody zaten tetikleyecek
        }
    }, [isOpen, currentUserState?.companyId]);

    // requestBody değiştiğinde allCards'ı resetle (yeni sayfa için)
    useEffect(() => {
        if (requestBody?.page === 1 && isOpen) {
            setAllCards([]);
        }
    }, [requestBody?.page, isOpen]);

    // Yeni data geldiğinde allCards'a ekle
    useEffect(() => {
        if (fetchData && isOpen) {
            if (!fetchData.success || fetchData.data === null || fetchData.data === undefined) {
                setHasMore(false);
                setIsLoadingMore(false);
                isLoadingMoreRef.current = false;
                if (currentPage === 1) {
                    setAllCards([]);
                }
                return;
            }
            
            let newCards: any[] = [];
            if (Array.isArray(fetchData.data)) {
                newCards = fetchData.data;
            } else if (fetchData.data?.data && Array.isArray(fetchData.data.data)) {
                newCards = fetchData.data.data;
            }
            
            if (Array.isArray(newCards) && newCards.length > 0) {
                if (currentPage === 1) {
                    setAllCards(newCards);
                } else {
                    setAllCards(prev => {
                        const existingIds = new Set(prev.map((card: any) => card.id || card.cardNo));
                        const uniqueNewCards = newCards.filter((card: any) => !existingIds.has(card.id || card.cardNo));
                        return [...prev, ...uniqueNewCards];
                    });
                }
                setHasMore(newCards.length >= 25);
            } else {
                if (currentPage === 1) {
                    setAllCards([]);
                }
                setHasMore(false);
            }
            setIsLoadingMore(false);
            isLoadingMoreRef.current = false;
        }
    }, [fetchData, currentPage, isOpen]);

    // Kartları filtreleme
    const filteredCards = allCards.filter((card: any) => {
        if (!card || !card.cardNo) return false;
        const searchLower = cardSearchText.toLowerCase();
        return card.cardNo.toLowerCase().includes(searchLower) ||
            (card.embossName && card.embossName.toLowerCase().includes(searchLower)) ||
            (card.cardName && card.cardName.toLowerCase().includes(searchLower)) ||
            (card.customerName && card.customerName.toLowerCase().includes(searchLower));
    });

    // Sonraki sayfayı yükle
    const loadNextPage = () => {
        if (!hasMore || isLoadingMore || isRequestLoading || isLoadingMoreRef.current) {
            return;
        }
        isLoadingMoreRef.current = true;
        setIsLoadingMore(true);
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        setRequestBody(prev => ({ ...prev, page: nextPage }));
        // Manuel refetch'i kaldırıyoruz çünkü setRequestBody zaten tetikleyecek
    };


    const handleConfirm = async () => {
        if (!selectedCard) {
            enqueueSnackbar('Lütfen kart seçiniz', {
                variant: 'warning'
            });
            return;
        }

        if (!expiryMonth || !expiryYear) {
            enqueueSnackbar('Lütfen son kullanma tarihini seçiniz', {
                variant: 'warning'
            });
            return;
        }

        if (!cvv || cvv.length !== 3) {
            enqueueSnackbar('Lütfen 3 haneli CVV kodunu giriniz', {
                variant: 'warning'
            });
            return;
        }

        try {
            setLoading(true);

            const activationInfo = {
                cardNo: encrypt(selectedCard.cardNo),
                expiryDate: encrypt(expiryYear + String(expiryMonth || '').padStart(2, '0')),
                cvv: encrypt(cvv),
                companyId: currentUserState?.companyId || undefined
            };

            const response: any = await updateCardStatus(activationInfo);

            if (response?.data?.success) {
                enqueueSnackbar(response?.data?.message, {
                    variant: 'success'
                });
                // Formu temizle
                setSelectedCard(null);
                setExpiryMonth('');
                setExpiryYear('');
                setCvv('');
                handleClose();
                setIsSetPasswordModalOpen(true);
            }
        } catch (error: any) {
            const errorMessage = error?.error?.message || error?.message || 'Bilinmeyen bir hata oluştu';
            enqueueSnackbar(errorMessage, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setSelectedCard(null);
        setCardSearchText('');
        setCurrentPage(1);
        setAllCards([]);
        setHasMore(true);
        setIsLoadingMore(false);
        isLoadingMoreRef.current = false;
        setRequestBody(prev => ({ ...prev, page: 1 }));
        setExpiryMonth('');
        setExpiryYear('');
        setCvv('');
        onClose();
    };

    // Ay ve yıl seçenekleri için yardımcı fonksiyonlar
    const getAvailableMonths = () => {
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + 1; // getMonth() 0-based, biz 1-based istiyoruz

        if (!expiryYear || parseInt(expiryYear) > currentYear) {
            // Seçili yıl gelecek yıl ise tüm ayları göster
            return Array.from({ length: 12 }, (_, i) => i + 1);
        } else if (parseInt(expiryYear) === currentYear) {
            // Seçili yıl mevcut yıl ise sadece kalan ayları göster
            return Array.from({ length: 12 - currentMonth + 1 }, (_, i) => currentMonth + i);
        }
        return [];
    };

    const getAvailableYears = () => {
        const currentYear = new Date().getFullYear();
        return Array.from({ length: 6 }, (_, i) => currentYear + i); // 2025-2030
    };

    const getMonthName = (month: number) => {
        const months = [
            'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
            'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
        ];
        return months[month - 1];
    };

    return (
        <>
            <ConfirmationModal
                isOpen={isOpen}
                onClose={handleClose}
                onConfirm={handleConfirm}
                title="Kart Aktive Et"
                isLoading={loading}
                confirmButtonText={'Kaydet'}
                closeButtonText="Vazgeç"
                optionalWidth="500px"
                customHeaderStyle={{
                    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                    titleColor: 'white',
                    icon: (
                        <Avatar sx={{
                            background: 'rgba(255,255,255,0.2)',
                            color: 'white',
                            width: 32,
                            height: 32
                        }}>
                            <CheckCircle fontSize="small" />
                        </Avatar>
                    )
                }}
            >
                <Typography variant="body1" sx={{ mb: 3, color: '#64748b' }}>
                    Aktif etmek istediğiniz kartı seçiniz:
                </Typography>

                <Box
                    {...bindTrigger(popupState)}
                    sx={{
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        padding: '8px 12px',
                        minHeight: '56px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        cursor: 'pointer',
                        '&:hover': {
                            borderColor: 'primary.main'
                        },
                        position: 'relative',
                        pr: 5
                    }}
                >
                    {!selectedCard ? (
                        <Typography sx={{ color: 'text.secondary', alignSelf: 'center' }}>
                            Kart arayın veya seçin
                        </Typography>
                    ) : (
                        <Chip
                            key={selectedCard.cardNo}
                            variant="outlined"
                            size="small"
                            color="primary"
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {selectedCard.cardNo}
                                    </Typography>
                                    {(selectedCard.embossName || selectedCard.cardName || selectedCard.customerName) && (
                                        <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                                            - {selectedCard.embossName || selectedCard.cardName || selectedCard.customerName}
                                        </Typography>
                                    )}
                                </Box>
                            }
                            onDelete={(e) => {
                                e.stopPropagation();
                                setSelectedCard(null);
                            }}
                            sx={{
                                '& .MuiChip-deleteIcon': {
                                    fontSize: '18px',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        color: '#ffffff !important',
                                        backgroundColor: '#d32f2f !important',
                                        borderRadius: '50%',
                                        transform: 'scale(1.1)'
                                    }
                                }
                            }}
                        />
                    )}
                    <Box sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', display: 'flex', gap: 0.5 }}>
                        {isRequestLoading && currentPage === 1 && <CircularProgress size={20} color="inherit" />}
                        <KeyboardArrowDown sx={{ color: 'text.secondary' }} />
                    </Box>
                </Box>

                <Popover
                    {...bindPopover(popupState)}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    PaperProps={{
                        sx: { width: 500, mt: 1, maxHeight: 450, overflow: 'hidden', display: 'flex', flexDirection: 'column' }
                    }}
                >
                    {isRequestLoading && currentPage === 1 ? (
                        <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                            <CircularProgress size={40} thickness={4} sx={{ color: '#3b82f6' }} />
                            <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                                Kartlar Yükleniyor...
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Kart No veya İsim ile ara..."
                                    value={cardSearchText}
                                    onChange={(e) => setCardSearchText(e.target.value)}
                                    autoFocus
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search fontSize="small" color="action" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: cardSearchText && (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => setCardSearchText('')}>
                                                    <Close fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Box>
                            <List
                                sx={{ 
                                    flexGrow: 1, 
                                    overflowY: 'auto', 
                                    maxHeight: 300,
                                    '&::-webkit-scrollbar': {
                                        width: '8px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: '#f1f1f1',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: '#ccc',
                                        borderRadius: '4px',
                                    },
                                    '&::-webkit-scrollbar-thumb:hover': {
                                        background: '#aaa',
                                    }
                                }}
                                onScroll={(e) => {
                                    const target = e.currentTarget;
                                    if (isRequestLoading || isLoadingMore || !hasMore || isLoadingMoreRef.current) {
                                        return;
                                    }
                                    if (target.scrollHeight - target.scrollTop <= target.clientHeight + 30) {
                                        loadNextPage();
                                    }
                                }}
                            >
                                {filteredCards.length === 0 && !isRequestLoading ? (
                                    <Box sx={{ p: 2, textAlign: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Kart bulunamadı
                                        </Typography>
                                    </Box>
                                ) : (
                                    filteredCards.map((card) => (
                                        <ListItem 
                                            key={card.cardNo} 
                                            button 
                                            onClick={() => toggleCard(card)}
                                            sx={{ py: 0.5 }}
                                        >
                                            <Checkbox 
                                                edge="start" 
                                                checked={isCardSelected(card.cardNo)} 
                                                tabIndex={-1} 
                                                disableRipple 
                                            />
                                            <ListItemText 
                                                primary={card.cardNo} 
                                                secondary={card.cardName || card.embossName || card.customerName}
                                                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                                secondaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.8rem' } }}
                                            />
                                            <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto', pl: 2, fontWeight: 600, fontSize: '0.85rem' }}>
                                                {card.customerName || card.embossName || card.cardName}
                                            </Typography>
                                        </ListItem>
                                    ))
                                )}
                                {(isLoadingMore || isLoadingMoreRef.current || (isRequestLoading && currentPage > 1)) && hasMore && (
                                    <ListItem sx={{ 
                                        display: 'flex', 
                                        flexDirection: 'column',
                                        justifyContent: 'center', 
                                        alignItems: 'center', 
                                        py: 2,
                                        background: 'linear-gradient(to bottom, transparent, rgba(59, 130, 246, 0.05))',
                                        borderTop: '1px solid #f1f5f9',
                                        width: '100%'
                                    }}>
                                        <Box sx={{ position: 'relative', display: 'inline-flex', mb: 1 }}>
                                            <CircularProgress 
                                                size={24} 
                                                thickness={4} 
                                                sx={{ color: '#e2e8f0' }} 
                                                variant="determinate" 
                                                value={100} 
                                            />
                                            <CircularProgress 
                                                size={24} 
                                                thickness={4} 
                                                sx={{ 
                                                    color: '#3b82f6', 
                                                    position: 'absolute', 
                                                    left: 0,
                                                    animationDuration: '800ms'
                                                }} 
                                            />
                                        </Box>
                                        <Typography 
                                            variant="caption" 
                                            sx={{ 
                                                color: '#3b82f6', 
                                                fontWeight: 700, 
                                                letterSpacing: '0.5px',
                                                textTransform: 'uppercase',
                                                fontSize: '0.65rem'
                                            }}
                                        >
                                            Kartlar Yükleniyor
                                        </Typography>
                                    </ListItem>
                                )}
                            </List>
                        </>
                    )}
                </Popover>

                {/* Son Kullanma Tarihi ve CVV */}
                <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                        Kart Bilgileri
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Son Kullanma Yılı</InputLabel>
                                <Select
                                    value={expiryYear}
                                    onChange={(e) => {
                                        setExpiryYear(e.target.value);
                                        // Yıl değiştiğinde ay seçimini resetle
                                        setExpiryMonth('');
                                    }}
                                    label="Son Kullanma Yılı"
                                >
                                    {getAvailableYears().map((year) => (
                                        <MenuItem key={year} value={year.toString()}>
                                            {year}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Son Kullanma Ayı</InputLabel>
                                <Select
                                    value={expiryMonth}
                                    onChange={(e) => setExpiryMonth(e.target.value)}
                                    label="Son Kullanma Ayı"
                                    disabled={!expiryYear}
                                >
                                    {getAvailableMonths().map((month) => (
                                        <MenuItem key={month} value={month.toString()}>
                                            {month.toString().padStart(2, '0')} - {getMonthName(month)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                size="small"
                                label="CVV"
                                value={cvv}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, '');
                                    setCvv(value.slice(0, 3));
                                }}
                                placeholder="123"
                                inputProps={{
                                    maxLength: 3,
                                    style: { letterSpacing: '0.2em', textAlign: 'center' }
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Typography variant="caption" sx={{ color: '#64748b', mt: 1, display: 'block' }}>
                        CVV kodu kartınızın arkasında bulunan 3 haneli güvenlik kodudur.
                    </Typography>
                </Box>
            </ConfirmationModal>

            <SetPasswordAction
                isOpen={isSetPasswordModalOpen}
                onClose={() => setIsSetPasswordModalOpen(false)}
                onSuccess={() => {
                    setIsSetPasswordModalOpen(false);
                    onSuccess?.();
                }}
            />
        </>
    );
};

export default ActivateCardAction;
